import { Suspense, useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { Bell, Check, CheckCheck, KeyRound, LogOut, Menu, Palette, User, X } from 'lucide-react';
import { useAuth } from '@/auth/AuthContext';
import { MODULES, type ModuleId } from '@/app/navigation';
import { THEMES } from '@/theme/themes';
import { cn, fromNow } from '@/lib/utils';
import { api } from '@/services/api';
import { invalidatePrefixes, useApi, type Paginated } from '@/services/query';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Avatar, Button, Dropdown, LoadingBlock, MenuItem } from '@/components/ui';

const LAST_MODULE = 'hrms.lastModule';

function useActiveModule(): ModuleId {
  const { pathname } = useLocation();
  const fromPath: ModuleId | null = pathname.startsWith('/tasks') ? 'tasks' : pathname.startsWith('/hrms') ? 'hrms' : null;
  useEffect(() => {
    if (fromPath) sessionStorage.setItem(LAST_MODULE, fromPath);
  }, [fromPath]);
  return fromPath ?? ((sessionStorage.getItem(LAST_MODULE) as ModuleId) || 'hrms');
}

interface NotificationRow {
  id: string;
  type: string;
  title: string;
  body: string | null;
  link: string | null;
  readAt: string | null;
  createdAt: string;
}

function NotificationBell() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const count = useApi<{ unreadCount: number }>('/notifications/unread-count', undefined, { refetchInterval: 30_000 });
  const unread = count.data?.unreadCount ?? 0;
  return (
    <Dropdown
      trigger={() => (
        <Button variant="ghost" size="icon" aria-label={`Notifications${unread ? `, ${unread} unread` : ''}`} className="relative">
          <Bell className="h-[18px] w-[18px]" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-bold text-white tabular-nums animate-scale-in">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </Button>
      )}
      className="w-[360px] max-w-[calc(100vw-1rem)] p-0 max-sm:fixed max-sm:top-14 max-sm:right-2 max-sm:left-2 max-sm:w-auto max-sm:max-w-none"
    >
      {(close) => <NotificationPanel close={close} onChanged={() => invalidatePrefixes(qc, ['/notifications'])} navigate={navigate} />}
    </Dropdown>
  );
}

function NotificationPanel({ close, onChanged, navigate }: { close: () => void; onChanged: () => void; navigate: (to: string) => void }) {
  const list = useApi<Paginated<NotificationRow> & { unreadCount: number }>('/notifications', { pageSize: 8 }, { keepPrevious: false });
  const open = async (n: NotificationRow) => {
    if (!n.readAt) await api.patch(`/notifications/${n.id}/read`, { read: true });
    onChanged();
    close();
    if (n.link) navigate(n.link);
  };
  return (
    <div>
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <p className="text-sm font-semibold">Notifications</p>
        <Button
          variant="ghost"
          size="sm"
          icon={<CheckCheck className="h-3.5 w-3.5" />}
          onClick={async () => {
            await api.post('/notifications/read-all');
            onChanged();
          }}
        >
          Mark all read
        </Button>
      </div>
      <div className="scrollbar-thin max-h-[420px] overflow-y-auto">
        {list.isLoading ? (
          <LoadingBlock rows={3} />
        ) : list.data?.items.length ? (
          list.data.items.map((n) => (
            <button key={n.id} onClick={() => open(n)} className={cn('flex w-full gap-3 border-b border-border px-4 py-3 text-left transition-colors last:border-0 hover:bg-surface-2', !n.readAt && 'bg-primary-soft/40')}>
              <span className={cn('mt-1.5 h-2 w-2 shrink-0 rounded-full', n.readAt ? 'bg-transparent' : 'bg-primary')} aria-label={n.readAt ? 'Read' : 'Unread'} />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium">{n.title}</span>
                {n.body && <span className="mt-0.5 line-clamp-2 block text-xs text-muted">{n.body}</span>}
                <span className="mt-1 block text-[11px] text-subtle">{fromNow(n.createdAt)}</span>
              </span>
            </button>
          ))
        ) : (
          <p className="px-4 py-8 text-center text-sm text-muted">You're all caught up.</p>
        )}
      </div>
      <Link to="/notifications" onClick={close} className="block border-t border-border px-4 py-2.5 text-center text-xs font-medium text-primary hover:bg-surface-2">
        View all notifications
      </Link>
    </div>
  );
}

function ThemeMenu() {
  const { user, setTheme, can } = useAuth();
  if (!can('settings:manage')) return null;
  return (
    <Dropdown
      trigger={() => (
        <Button variant="ghost" size="icon" aria-label="Choose theme">
          <Palette className="h-[18px] w-[18px]" />
        </Button>
      )}
    >
      {(close) => (
        <div className="w-56 p-1" role="menu" aria-label="Themes">
          <p className="px-3 pt-2 text-xs font-medium text-muted">Organisation theme</p>
          <p className="px-3 pb-2 text-[11px] text-subtle">Applies to everyone instantly.</p>
          {THEMES.map((t) => (
            <MenuItem
              key={t.id}
              onClick={() => {
                setTheme(t.id).catch((e: Error) => toast.error(e.message));
                close();
              }}
              icon={
                <span className="h-5 w-5 shrink-0 rounded-full ring-2 ring-white/70 shadow-sm" style={{ background: t.color }} aria-hidden />
              }
            >
              <span className="flex-1">{t.name}</span>
              {user?.company.theme === t.id && <Check className="h-4 w-4 text-primary" />}
            </MenuItem>
          ))}
        </div>
      )}
    </Dropdown>
  );
}

function UserMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;
  return (
    <Dropdown
      trigger={() => (
        <button className="flex items-center gap-2 rounded-lg px-1.5 py-1 hover:bg-surface-2" aria-label="Account menu">
          <Avatar name={user.employee.fullName} size={32} />
          <span className="hidden text-left md:block">
            <span className="block max-w-[140px] truncate text-sm leading-tight font-medium">{user.employee.fullName}</span>
            <span className="block text-[11px] leading-tight text-muted">{user.roles.map((r) => r.name).join(', ')}</span>
          </span>
        </button>
      )}
    >
      {(close) => (
        <div className="w-60 p-1">
          <div className="border-b border-border px-3 py-2">
            <p className="truncate text-sm font-medium">{user.employee.fullName}</p>
            <p className="truncate text-xs text-muted">{user.email}</p>
          </div>
          <MenuItem icon={<User className="h-4 w-4" />} onClick={() => (close(), navigate('/profile'))}>
            My profile
          </MenuItem>
          <MenuItem icon={<KeyRound className="h-4 w-4" />} onClick={() => (close(), navigate('/profile?tab=security'))}>
            Change password
          </MenuItem>
          <MenuItem
            danger
            icon={<LogOut className="h-4 w-4" />}
            onClick={async () => {
              close();
              await logout();
              navigate('/login', { replace: true });
            }}
          >
            Sign out
          </MenuItem>
        </div>
      )}
    </Dropdown>
  );
}

function Sidebar({ module, onNavigate }: { module: ModuleId; onNavigate?: () => void }) {
  const { can } = useAuth();
  const m = MODULES.find((x) => x.id === module)!;
  const items = m.nav.filter((i) => can(...i.perms));
  const sections = useMemo(() => {
    const out: { name?: string; items: typeof items }[] = [];
    for (const i of items) {
      const last = out[out.length - 1];
      if (last && last.name === i.section) last.items.push(i);
      else out.push({ name: i.section, items: [i] });
    }
    return out;
  }, [items]);
  return (
    <nav aria-label={`${m.label} navigation`} className="scrollbar-thin flex-1 space-y-5 overflow-y-auto px-3 py-4">
      {sections.map((s, idx) => (
        <div key={idx} className="animate-fade-in" style={{ animationDelay: `${idx * 25}ms` }}>
          {s.name && <p className="mb-1.5 px-3 text-[10px] font-semibold tracking-wider text-sidebar-text/60 uppercase">{s.name}</p>}
          <ul className="space-y-0.5">
            {s.items.map((i) => (
              <li key={i.to}>
                <NavLink
                  to={i.to}
                  end={i.to.endsWith('dashboard')}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    cn(
                      'group flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all duration-150',
                      isActive ? 'bg-sidebar-active font-medium text-white' : 'text-sidebar-text hover:bg-white/5 hover:text-white',
                    )
                  }
                >
                  <i.icon className="h-4 w-4 shrink-0 transition-transform duration-150 group-hover:scale-110" aria-hidden />
                  <span className="truncate">{i.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}

export function AppShell() {
  const { user, can } = useAuth();
  const module = useActiveModule();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => setMobileOpen(false), [location.pathname]);
  const modules = MODULES.filter((m) => m.nav.some((i) => can(...i.perms)));

  return (
    <div className="flex h-dvh">
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-primary-fg">
        Skip to content
      </a>
      <aside className="hidden w-64 shrink-0 flex-col bg-sidebar lg:flex">
        <Brand />
        <Sidebar module={module} />
        <SidebarFooter />
      </aside>
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50 animate-fade-in" onClick={() => setMobileOpen(false)} aria-hidden />
          <aside className="absolute inset-y-0 left-0 flex w-72 flex-col bg-sidebar shadow-2xl animate-fade-in">
            <div className="flex items-center justify-between pr-3">
              <Brand />
              <Button variant="ghost" size="icon" className="text-sidebar-text" onClick={() => setMobileOpen(false)} aria-label="Close menu">
                <X className="h-5 w-5" />
              </Button>
            </div>
            <ModuleTabs modules={modules} active={module} className="mx-3 mb-2 sm:hidden" />
            <Sidebar module={module} onNavigate={() => setMobileOpen(false)} />
            <SidebarFooter />
          </aside>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center gap-1.5 border-b border-border bg-surface/85 px-2 backdrop-blur sm:h-16 sm:gap-3 sm:px-5">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(true)} aria-label="Open menu">
            <Menu className="h-5 w-5" />
          </Button>
          <ModuleTabs modules={modules} active={module} className="hidden sm:inline-flex" />
          <ModuleTabs modules={modules} active={module} compact className="sm:hidden" />
          <div className="flex-1" />
          <span className="hidden text-xs text-muted xl:inline">{user?.company.name}</span>
          <NotificationBell />
          <ThemeMenu />
          <UserMenu />
        </header>
        <main id="main" className="scrollbar-thin flex-1 overflow-y-auto bg-surface">
          <div key={location.pathname} className="w-full px-3 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] animate-fade-in sm:px-6 sm:py-5">
            <Suspense fallback={<LoadingBlock rows={6} />}>
              <Outlet />
            </Suspense>
          </div>
        </main>
      </div>
    </div>
  );
}

function ModuleTabs({ modules, active, className, compact }: { modules: typeof MODULES; active: ModuleId; className?: string; compact?: boolean }) {
  return (
    <nav aria-label="Modules" className={cn('inline-flex rounded-xl border border-border bg-surface-2 p-1', className)}>
      {modules.map((m) => (
        <Link
          key={m.id}
          to={m.home}
          aria-current={active === m.id ? 'page' : undefined}
          className={cn(
            'inline-flex flex-1 items-center justify-center gap-2 rounded-lg py-1.5 text-sm font-medium transition-all duration-200',
            compact ? 'px-2.5' : 'px-4',
            active === m.id ? 'bg-primary text-primary-fg shadow-sm' : 'text-muted hover:text-text',
          )}
        >
          <m.icon className="h-4 w-4" aria-hidden />
          {compact ? <span className="sr-only">{m.label}</span> : m.label}
        </Link>
      ))}
    </nav>
  );
}

function Brand() {
  const { user } = useAuth();
  return (
    <Link to="/" className="flex h-16 items-center gap-3 px-5">
      <img src="/favicon.svg" alt="" className="h-8 w-8" />
      <span className="min-w-0">
        <span className="block truncate text-sm font-semibold text-white">Unified HRMS</span>
        <span className="block truncate text-[11px] text-sidebar-text">{user?.company.name ?? 'HR & Task Platform'}</span>
      </span>
    </Link>
  );
}

function SidebarFooter() {
  const { user } = useAuth();
  return (
    <div className="border-t border-white/10 px-5 py-3 text-[11px] text-sidebar-text/70">
      <p className="truncate">Signed in as {user?.email}</p>
      <p>Timezone: {user?.company.timezone}</p>
    </div>
  );
}
